import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useBusiness } from '../../features/onboarding/useBusiness';
import { apiClient } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { QuotaRead } from '../../types/api';
import { QuotaContext, type QuotaContextValue } from './quotaContext';

/** Read-only: nothing anywhere increments a quota — usage is counted from rows. */
function fetchQuota(businessId: string, signal?: AbortSignal): Promise<QuotaRead> {
  return apiClient.get<QuotaRead>(`/api/v1/businesses/${businessId}/quota`, { signal });
}

/** Result of the last completed read, pinned to the business it was read for. */
interface LoadedQuota {
  businessId: string;
  quota: QuotaRead | null;
}

/**
 * Loads this month's generation allowance and shares it with the header counter
 * and every screen that spends one.
 *
 * A failed read is not fatal and never blocks: the counter falls back to
 * `--/--` and the user can still submit. Enforcement is the backend's — it
 * answers 429 `QUOTA_EXCEEDED` on the two actions that start a pipeline run —
 * so a client that guessed wrong costs nothing but a message.
 */
export function QuotaProvider({ children }: { children: ReactNode }) {
  const { businessId } = useBusiness();
  const [loaded, setLoaded] = useState<LoadedQuota | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!businessId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const quota = await fetchQuota(businessId, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, quota });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        // Fail soft: an unknown allowance shows as unknown, not as zero.
        console.warn('Quota lookup failed.', getErrorMessage(caught));
        setLoaded({ businessId, quota: null });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, nonce]);

  const value = useMemo<QuotaContextValue>(() => {
    // A stale reading from a previous business is filtered out during render,
    // the same way `AccountProvider` guards its own cache.
    const isCurrent = loaded !== null && loaded.businessId === businessId;
    return {
      quota: isCurrent ? loaded.quota : null,
      isLoading: businessId !== null && !isCurrent,
      refresh,
    };
  }, [loaded, businessId, refresh]);

  return <QuotaContext.Provider value={value}>{children}</QuotaContext.Provider>;
}
